import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../auth/useAuth";
import { getMyDetails, updateMyProfile, leaveWorkspace } from "../../api/users/usersApi";
import { UserProfile, UserWorkspace } from "../common/ObjectTypes";
import { inputClasses, buttonClasses, containerClasses, skeletonClasses } from "../common/tailwindClassnames";
import { HoverTooltip } from "../common/HoverTooltip";
import SelectInput from "../common/SelectInput";
import { AttentionIcon } from "../common/ProjectIcons";
import { portalLanguages } from "../common/ObjectTypes";
import { trackAccountProfileUpdated, trackAccountWorkspaceLeft } from "../../analytics/analytics";
import LeaveWorkspaceModal from "./LeaveWorkspaceModal";

const AccountPage = () => {
    const { t, i18n } = useTranslation();
    const { token, authenticated } = useAuth();

    const [pageLoaded, setPageLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [workspaces, setWorkspaces] = useState<UserWorkspace[]>([]);
    const [language, setLanguage] = useState(i18n.language);
    const [originalValues, setOriginalValues] = useState({ firstName: "", lastName: "", contactEmail: "", language: i18n.language });
    const languages = useMemo(() => portalLanguages(t), [t]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSuperuser, setIsSuperuser] = useState(false);
    const [leaveModalWorkspace, setLeaveModalWorkspace] = useState<UserWorkspace | null>(null);

    const changesMade = firstName !== originalValues.firstName
        || lastName !== originalValues.lastName
        || contactEmail !== originalValues.contactEmail
        || language !== originalValues.language;

    // True when the profile was loaded without a contact email — used to highlight
    // the field after the login-time redirect. Clears once the user saves a value.
    const contactEmailMissing = pageLoaded && originalValues.contactEmail === "";

    const populateForm = useCallback((profile: UserProfile) => {
        const fn = profile.first_name || "";
        const ln = profile.last_name || "";
        const ce = profile.contact_email || "";
        const lang = i18n.language;
        setFirstName(fn);
        setLastName(ln);
        setContactEmail(ce);
        setLanguage(lang);
        setWorkspaces(profile.workspaces || []);
        setIsSuperuser(!!profile.is_superuser);
        setOriginalValues({ firstName: fn, lastName: ln, contactEmail: ce, language: lang });
    }, []);

    useEffect(() => {
        const loadProfile = async () => {
            if (token) {
                try {
                    const profile = await getMyDetails(token);
                    if (profile) {
                        populateForm(profile);
                    }
                } catch {
                    // Error handled by API interceptor
                }
                setPageLoaded(true);
            }
        };

        if (!pageLoaded) {
            loadProfile();
        }
    }, [authenticated, token, pageLoaded, populateForm]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!firstName.trim()) {
            newErrors.firstName = t("This field is required.");
        }
        if (!lastName.trim()) {
            newErrors.lastName = t("This field is required.");
        }
        if (!contactEmail.trim()) {
            newErrors.contactEmail = t("This field is required.");
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())) {
            newErrors.contactEmail = t("Please enter a valid email address.");
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        if (!token) return;

        setSaving(true);
        try {
            const updatedProfile = await updateMyProfile(token, {
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                contact_email: contactEmail.trim(),
            });
            if (updatedProfile) {
                if (language !== originalValues.language) {
                    await i18n.changeLanguage(language);
                }
                populateForm(updatedProfile);
                trackAccountProfileUpdated();
                toast.success(t("Profile updated"), { position: toast.POSITION.TOP_RIGHT });
                window.dispatchEvent(new CustomEvent("userProfileUpdated", {
                    detail: { first_name: updatedProfile.first_name, last_name: updatedProfile.last_name, contact_email: updatedProfile.contact_email }
                }));
            }
        } catch (err: any) {
            if (err?.response?.status !== 401) {
                toast.error(t("Something went wrong updating your profile."), { position: toast.POSITION.TOP_RIGHT });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFirstName(originalValues.firstName);
        setLastName(originalValues.lastName);
        setContactEmail(originalValues.contactEmail);
        setLanguage(originalValues.language);
        setErrors({});
    };

    const handleLeaveWorkspace = async () => {
        if (!leaveModalWorkspace || !token) return;

        try {
            await leaveWorkspace(token, leaveModalWorkspace.workspace_id, leaveModalWorkspace.id);
            setWorkspaces(prev => prev.filter(w => w.workspace_id !== leaveModalWorkspace.workspace_id));
            trackAccountWorkspaceLeft(leaveModalWorkspace.name);
        } catch (err: any) {
            if (err?.response?.status === 404) {
                toast.error(t("Workspace not found or you are no longer a member."), { position: toast.POSITION.TOP_RIGHT });
            } else if (err?.response?.status !== 401) {
                toast.error(t("Something went wrong leaving the workspace."), { position: toast.POSITION.TOP_RIGHT });
            }
        } finally {
            setLeaveModalWorkspace(null);
        }
    };

    if (!pageLoaded) {
        const skeletonBar = (w: string, h: string = "h-4") => (
            <div className={`${h} ${w} ${skeletonClasses.base}`} />
        );
        return (
            <div className={containerClasses.page}>
                <div className="flex flex-row justify-center">
                    <div className={`${containerClasses.content} px-5 xl:px-12 pt-8 pb-56`}>
                        {skeletonBar("w-32", "h-8 mb-2")}
                        <div className="mb-6 space-y-1.5">
                            {skeletonBar("w-full", "h-3")}
                            {skeletonBar("w-3/4", "h-3")}
                        </div>
                        <div className="bg-white border border-gray-lines rounded-md p-4">
                            {/* First Name */}
                            <div className="flex flex-row items-center mb-6">
                                <div className="w-1/3">{skeletonBar("w-24")}</div>
                                <div className="w-2/5">{skeletonBar("w-full", "h-9")}</div>
                            </div>
                            {/* Last Name */}
                            <div className="flex flex-row items-center mb-6">
                                <div className="w-1/3">{skeletonBar("w-24")}</div>
                                <div className="w-2/5">{skeletonBar("w-full", "h-9")}</div>
                            </div>
                            {/* Workspaces */}
                            <div className="flex flex-row items-start mb-6">
                                <div className="w-1/3 mt-2">{skeletonBar("w-28")}</div>
                                <div className="w-2/3 space-y-2">
                                    {skeletonBar("w-full", "h-9")}
                                    {skeletonBar("w-3/4", "h-9")}
                                </div>
                            </div>
                            {/* Contact Email */}
                            <div className="flex flex-row items-center mb-6">
                                <div className="w-1/3">{skeletonBar("w-32")}</div>
                                <div className="w-2/5">{skeletonBar("w-full", "h-9")}</div>
                            </div>
                            {/* Language */}
                            <div className="flex flex-row items-center">
                                <div className="w-1/3">{skeletonBar("w-24")}</div>
                                <div className="w-2/5">{skeletonBar("w-full", "h-9")}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={containerClasses.page}>
            <div className="flex flex-row justify-center">
                <div className={`${containerClasses.content} px-5 xl:px-12 pt-8 pb-56`}>
                    <h1 className="text-3xl font-medium text-off-black mb-2">{t("Account")}</h1>
                    <p className="text-sm text-secondary-gray mb-6">{t("Keep your contact information up to date — it will be used for automatic alerts and notifications in the future. You can always return to this page by clicking the arrow next to your name and selecting Account.")}</p>

                    <div className="bg-white border border-gray-lines rounded-md p-4">
                        {/* First Name */}
                        <div className="flex flex-col justify-between md:flex-row md:items-start mb-6">
                            <label className="text-off-black md:w-1/3 mb-1 md:mb-0 md:pt-2">
                                {t("First Name")}*
                            </label>
                            <div className="md:w-2/5">
                                <input
                                    data-testid="AccountPage-firstName-input"
                                    className={`w-full ${errors.firstName ? inputClasses.error : inputClasses.standard}`}
                                    value={firstName}
                                    onChange={(e) => { setFirstName(e.target.value); setErrors(prev => ({ ...prev, firstName: "" })); }}
                                />
                                {errors.firstName && (
                                    <div className="text-orange flex flex-row items-center mt-1">
                                        <AttentionIcon /><p className="ml-1 text-sm">{errors.firstName}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Last Name */}
                        <div className="flex flex-col justify-between md:flex-row md:items-start mb-6">
                            <label className="text-off-black md:w-1/3 mb-1 md:mb-0 md:pt-2">
                                {t("Last Name")}*
                            </label>
                            <div className="md:w-2/5">
                                <input
                                    data-testid="AccountPage-lastName-input"
                                    className={`w-full ${errors.lastName ? inputClasses.error : inputClasses.standard}`}
                                    value={lastName}
                                    onChange={(e) => { setLastName(e.target.value); setErrors(prev => ({ ...prev, lastName: "" })); }}
                                />
                                {errors.lastName && (
                                    <div className="text-orange flex flex-row items-center mt-1">
                                        <AttentionIcon /><p className="ml-1 text-sm">{errors.lastName}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Workspaces */}
                        <div className="flex flex-col justify-between md:flex-row md:items-start mb-6">
                            <label className="text-off-black md:w-1/3 mb-1 md:mb-0 md:pt-2">
                                {t("Workspace(s)")}
                            </label>
                            <div className="md:w-2/3">
                                {workspaces.length > 0 ? (
                                    <div className="border border-gray-lines rounded-md overflow-visible">
                                        {workspaces.map((ws) => (
                                            <div
                                                key={ws.workspace_id}
                                                className="flex flex-row items-center border-b border-gray-lines last:border-b-0 px-3 py-2"
                                                data-testid={`AccountPage-workspace-${ws.workspace_id}`}
                                            >
                                                <Link
                                                    to={`/workspaces/${ws.workspace_id}`}
                                                    data-testid={`AccountPage-workspace-${ws.workspace_id}-link`}
                                                    className="flex-1 text-off-black truncate hover:text-dark-green hover:underline transition-colors duration-200"
                                                >
                                                    {ws.name}
                                                </Link>
                                                <span className="text-secondary-gray text-sm min-w-[60px] text-right mr-5">
                                                    {ws.role.charAt(0).toUpperCase() + ws.role.slice(1)}
                                                </span>
                                                {(ws.role === "admin" || isSuperuser) ? (
                                                    <button
                                                        type="button"
                                                        data-testid={`AccountPage-leaveWorkspace-${ws.workspace_id}-button`}
                                                        className={buttonClasses.lightRed}
                                                        onClick={() => setLeaveModalWorkspace(ws)}
                                                    >
                                                        {t("Leave")}
                                                    </button>
                                                ) : (
                                                    <HoverTooltip position="top-right" width="w-52" content={t("Only admins can manage workspace membership.")}>
                                                        <button
                                                            type="button"
                                                            data-testid={`AccountPage-leaveWorkspace-${ws.workspace_id}-button`}
                                                            disabled
                                                            className={buttonClasses.lightRed}
                                                        >
                                                            {t("Leave")}
                                                        </button>
                                                    </HoverTooltip>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-medium-gray text-sm">{t("No workspaces")}</p>
                                )}
                            </div>
                        </div>

                        {/* Contact Email */}
                        <div className="flex flex-col justify-between md:flex-row md:items-start mb-6">
                            <label className="text-off-black md:w-1/3 mb-1 md:mb-0 md:pt-2">
                                {t("Contact Email")}*
                            </label>
                            <div className="md:w-2/5">
                                {contactEmailMissing && (
                                    <div
                                        data-testid="AccountPage-contactEmail-missing-banner"
                                        className="flex flex-row items-center text-orange mb-2 text-sm"
                                    >
                                        <AttentionIcon />
                                        <p className="ml-1">{t("Please add a contact email to your account.")}</p>
                                    </div>
                                )}
                                <input
                                    type="email"
                                    data-testid="AccountPage-contactEmail-input"
                                    className={`w-full ${errors.contactEmail || contactEmailMissing ? inputClasses.error : inputClasses.standard}`}
                                    value={contactEmail}
                                    onChange={(e) => { setContactEmail(e.target.value); setErrors(prev => ({ ...prev, contactEmail: "" })); }}
                                />
                                {errors.contactEmail && (
                                    <div className="text-orange flex flex-row items-center mt-1">
                                        <AttentionIcon /><p className="ml-1 text-sm">{errors.contactEmail}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Language */}
                        <div className="flex flex-col justify-between md:flex-row md:items-start">
                            <label htmlFor="AccountPage-language-select" className="text-off-black md:w-1/3 mb-1 md:mb-0 md:pt-2">
                                {t("Language")}
                            </label>
                            <div className="md:w-2/5" data-testid="AccountPage-language-select">
                                <SelectInput
                                    inputId="AccountPage-language-select"
                                    options={languages}
                                    className="basic-single"
                                    value={languages.find(lang => lang.value === language) ?? null}
                                    isDisabled={false}
                                    isLoading={false}
                                    isClearable={false}
                                    isSearchable={false}
                                    isMulti={false}
                                    name="language"
                                    onChange={(e: any) => setLanguage(e.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer actions */}
                    <div className="w-full z-10 fixed bottom-0 mt-12 mr-6 py-3 w-full flex flex-row gap-3 bg-white/10 backdrop-blur">
                        <button
                            data-testid="AccountPage-save-button"
                            className={buttonClasses.primary}
                            disabled={!changesMade || saving}
                            onClick={handleSave}
                        >
                            {t("Save")}
                        </button>
                        <button
                            data-testid="AccountPage-cancel-button"
                            className={buttonClasses.cancel}
                            onClick={handleCancel}
                        >
                            {t("Cancel")}
                        </button>
                    </div>
                </div>
            </div>

            {leaveModalWorkspace && (
                <LeaveWorkspaceModal
                    workspaceName={leaveModalWorkspace.name}
                    onConfirm={handleLeaveWorkspace}
                    onCancel={() => setLeaveModalWorkspace(null)}
                />
            )}
        </div>
    );
};

export default AccountPage;
